import { Injectable } from "@angular/core";
import { NavController } from "@ionic/angular";
import { Router } from "@angular/router";
import { environment } from "src/environments/environment";
import { Position } from "../helpers/types";
import { ProviderService } from "./provider.service";
import { Capacitor } from "@capacitor/core";
import { SecureStoragePlugin } from "capacitor-secure-storage-plugin";
import { getUserDefaultsPlugin } from "./native-storage.plugin";

@Injectable({
  providedIn: "root",
})
export class LocalStorageService {
  url = `${environment.apiUrl}`;

  constructor(
    private navController: NavController,
    private router: Router,
    private providerService: ProviderService
  ) {}

  private persistNativeAuthData(token: string, role: string, refresh = "", client?: any): void {
    void this.persistNativeAuthDataAsync(token, role, refresh, client);
  }

  private async persistNativeAuthDataAsync(
    token: string,
    role: string,
    refresh = "",
    client?: any
  ): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const authToken = token || "";
    const userRole = String(role || "");
    if (!authToken || !userRole) {
      return;
    }

    const entries = [
      { key: "api_url", value: environment.authUrl },
      { key: "auth_token", value: authToken },
      { key: "user_role", value: userRole },
      { key: "refresh_token", value: refresh || "" },
      { key: "user_id", value: client?.id ? String(client.id) : "" },
      { key: "auth_user", value: client ? JSON.stringify(client) : "" },
    ];

    try {
      await Promise.all(
        entries
          .filter((entry) => entry.value !== "")
          .map((entry) => SecureStoragePlugin.set(entry))
      );
    } catch (_) {}

    try {
      const userDefaults = getUserDefaultsPlugin();
      await Promise.all(
        entries
          .filter((entry) => entry.value !== "")
          .map((entry) => userDefaults.set(entry))
      );
    } catch (_) {}
  }

  async getNativeAuthData(): Promise<{
    token: string;
    refresh: string;
    role: string;
    userId: string;
    user: any | null;
  }> {
    if (!Capacitor.isNativePlatform()) {
      return { token: "", refresh: "", role: "", userId: "", user: null };
    }

    const token = await this.readNativeValue("auth_token");
    const refresh = await this.readNativeValue("refresh_token");
    const role = await this.readNativeValue("user_role");
    const userId = await this.readNativeValue("user_id");
    const userJson = await this.readNativeValue("auth_user");

    let user: any | null = null;
    if (userJson) {
      try {
        user = JSON.parse(userJson);
      } catch (_) {
        user = null;
      }
    }

    return { token, refresh, role, userId, user };
  }

  private async readNativeValue(key: string): Promise<string> {
    try {
      const value = await SecureStoragePlugin.get({ key });
      if (value?.value) {
        return value.value;
      }
    } catch (_) {}

    try {
      const userDefaults = getUserDefaultsPlugin();
      const value = await userDefaults.get({ key });
      if (value?.value) {
        return value.value;
      }
    } catch (_) {}

    return "";
  }

  clearNativeAuthData(): void {
    void this.clearNativeAuthDataAsync();
  }

  private async clearNativeAuthDataAsync(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const keys = [
      "api_url",
      "auth_token",
      "user_role",
      "refresh_token",
      "user_id",
      "auth_user",
      "provider_current_state",
      "native_location_command",
      "native_pusher_command",
      "was_tracking",
      "was_listening",
    ];

    try {
      const result = await SecureStoragePlugin.keys();
      const existingKeys = Array.isArray(result?.value) ? result.value : [];
      await Promise.all(
        keys
          .filter((key) => existingKeys.includes(key))
          .map((key) => SecureStoragePlugin.remove({ key }))
      );
    } catch (_) {}

    try {
      const userDefaults = getUserDefaultsPlugin();
      await Promise.all(
        keys.map((key) =>
          typeof userDefaults.remove === "function"
            ? userDefaults.remove({ key })
            : userDefaults.set({ key, value: "" })
        )
      );
    } catch (_) {}
  }

  /*==============================
  save user 
  ================================*/
  saveAuthData(token: string, client: any) {
    let roles = client.roles;
    let userId = client.id;
    localStorage.setItem("token", token);
    localStorage.setItem("role", roles[0]);
    localStorage.setItem("user_id", userId);
    localStorage.setItem("user", JSON.stringify(client));
    this.persistNativeAuthData(token, roles[0], "", client);
  }
  saveAuthDataFromLogin(token: string, client: any, role: any, refresh: any) {
    let userId = client.id;
    localStorage.setItem("token", token);
    localStorage.setItem("refresh", refresh);
    localStorage.setItem("role", role);
    localStorage.setItem("user_id", userId);
    localStorage.setItem("user", JSON.stringify(client));
    this.persistNativeAuthData(token, role, refresh, client);
  }

  /*==============================
  firebase data
  ================================*/
  setFcmToken(fcm: string) {
    localStorage.setItem("FCM_TOKEN", fcm);
  }
  getFcmToken(): string {
    let fcm_token = localStorage.getItem("FCM_TOKEN");
    return fcm_token ? fcm_token : "";
  }

  removebackUrl() {
    localStorage.removeItem("blockedRoute");
  }
  backUrl(val: string) {
    localStorage.setItem("blockedRoute", val.toString());
  }
  getBackUrl(): string {
    let back = localStorage.getItem("blockedRoute");
    return back && back?.length ? back : "";
  }

  /** Spécialité choisie sur le formulaire avant redirection login (restaurée au retour). */
  setPendingDemandeSpecialtyId(id: string | number | null): void {
    const key = "pending_demande_specialty_id";
    if (id == null || id === "") {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, String(id));
    }
  }
  getPendingDemandeSpecialtyId(): string | null {
    return localStorage.getItem("pending_demande_specialty_id");
  }
  removePendingDemandeSpecialtyId(): void {
    localStorage.removeItem("pending_demande_specialty_id");
  }

  /*==============================
  get user 
  ================================*/
  getUserData() {
    const userDate = localStorage.getItem("user");
    const userToken = localStorage.getItem("token");
    if (userDate && userToken) {
      return {
        client: JSON.parse(userDate),
        token: userToken,
      };
    } else {
      return {
        client: false,
        token: false,
      };
    }
  }
  getToken() {
    let token = localStorage.getItem("token")
      ? localStorage.getItem("token")
      : "";
    return token;
  }
  getRefreshToken() {
    let token = localStorage.getItem("refresh")
      ? localStorage.getItem("refresh")
      : "";
    return token;
  }
  isAuth() {
    let token = localStorage.getItem("token") ? true : false;
    return token;
  }
  updateUser(client: any) {
    localStorage.setItem("user", JSON.stringify(client));
  }
  updateUserImage(image: any) {
    let currentUser: any = JSON.parse(localStorage.getItem("user") || "");
    currentUser.image = image;
    localStorage.setItem("user", JSON.stringify(currentUser));
  }
  hasRequiredRoles(userRoles: any): boolean {
    const currentUser: any = this.getUserData();
    if (!currentUser.client) {
      return false;
    } else {
      const roles = currentUser?.client.roles;
      return roles.every((role: any) => userRoles.includes(role));
    }
  }
  getUserRole() {
    let role = localStorage.getItem("role");
    return role ? role : "";
  }

  /*==============================
  save location 
  ================================*/
  setLocation(coords: Position) {
    localStorage.setItem("user_location", JSON.stringify(coords));
  }
  /*==============================
  save location 
  ================================*/
  getLocation() {
    const currentLocation = localStorage.getItem("user_location");
    return currentLocation ? JSON.parse(currentLocation) : null;
  }
  getActiveStatus() {
    const userString = localStorage.getItem("user");
    const user = userString ? JSON.parse(userString) : null;
    const statut_pro = user.statut_pro;
    return statut_pro;
  }

  getTransporterServiceOpen() {
    const transporter = localStorage.getItem("settings");
    return transporter ? JSON.parse(transporter).is_transporter : null;
  }
  /*==============================
  transporter data 
  ================================*/
  setTransporterDate(data: any) {
    localStorage.setItem("transporter", JSON.stringify(data));
  }
  getTransporterDate() {
    const transporter = localStorage.getItem("transporter");
    return transporter ? JSON.parse(transporter) : null;
  }
  /*==============================
  save domain 
  ================================*/
  setDomain(domain: any) {
    localStorage.setItem("domain", JSON.stringify(domain));
  }
  /*==============================
  get domain 
  ================================*/
  getDomain() {
    const domain = localStorage.getItem("domain");
    return domain ? JSON.parse(domain) : null;
  }
  logout() {
    void import("./background-location.service")
      .then((m) => m.BackgroundLocationNativePlugin.stopPresencePing())
      .catch(() => {});
    try {
      const current_position = localStorage.getItem("user_location");
      const splash_viewed = localStorage.getItem("splash_viewed");
      const storedAccounts = localStorage.getItem("cap_sec_accounts");
      const locationDisclosure = localStorage.getItem("location_disclosure_accepted");
      const locationDisclosureDeclined = localStorage.getItem("location_disclosure_declined");

      this.clearNativeAuthData();
      localStorage.clear();

      if (current_position) {
        localStorage.setItem("user_location", current_position);
      }
      if (splash_viewed) {
        localStorage.setItem("splash_viewed", splash_viewed);
      }
      if (storedAccounts) {
        localStorage.setItem("cap_sec_accounts", storedAccounts);
      }
      if (locationDisclosure) {
        localStorage.setItem("location_disclosure_accepted", locationDisclosure);
      }
      if (locationDisclosureDeclined) {
        localStorage.setItem("location_disclosure_declined", locationDisclosureDeclined);
      }
      
      // Use NavController directly to avoid circular dependency with NavigationService
      this.navController.navigateRoot(["/login"], { 
        queryParams: { 
          reason: 'session_expired',
          message: 'Votre session a expiré. Veuillez vous reconnecter.'
        }
      });
    } catch (error) {
      // Fallback: use router for navigation
      this.navController.navigateRoot(["/login"]);
    }
  }
  /*==============================
  save active demand 
  ================================*/
  setToCreateDemand(demand: any) {
    localStorage.setItem("demand_to_create", JSON.stringify(demand));
  }
  removeToCreateDemand() {
    localStorage.removeItem("demand_to_create");
  }
  getToCreateDemand() {
    const current_demand = localStorage.getItem("demand_to_create");
    return current_demand ? JSON.parse(current_demand) : null;
  }
  /*==============================
  save active demand 
  ================================*/
  setCurrentDemand(demand: any) {
    localStorage.setItem("current_demand", JSON.stringify(demand));
  }

  dropAcceptationCoords() {
    localStorage.removeItem("acceptation_coords");
  }
  setAcceptationCoords(coords: any) {
    localStorage.setItem("acceptation_coords", JSON.stringify(coords));
  }
  getAcceptationCoords() {
    const acceptation_coords = localStorage.getItem("acceptation_coords");
    return acceptation_coords ? JSON.parse(acceptation_coords) : null;
  }

  /*==============================
  get active demand 
  ================================*/
  getCurrentDemand() {
    const current_demand = localStorage.getItem("current_demand");
    return current_demand ? JSON.parse(current_demand) : null;
  }
  /*==============================
  drop saved demand
  ================================*/
  /**
   * Provider-side: clears provider's "current accepted demand" state INCLUDING
   * the ProviderService BehaviorSubject. Do NOT call from client-side flows
   * (use dropClientDemand for that) — sharing this method across roles
   * caused the provider's in-memory demand to be reset when a particulier
   * cancelled their own demand, corrupting provider UI on multi-role devices.
   */
  dropCurrentDemand() {
    this.providerService.storeDemandData(null);
    localStorage.removeItem("current_demand");
    localStorage.removeItem("devis");
    localStorage.removeItem("acceptation_coords");
  }
  /**
   * Client-side cleanup: removes the persisted demand without touching the
   * provider-side ProviderService BehaviorSubject.
   */
  dropClientDemand() {
    localStorage.removeItem("current_demand");
    localStorage.removeItem("devis");
    localStorage.removeItem("acceptation_coords");
  }
  /*==============================
  save devis 
  ================================*/
  setDevis(data: any) {
    localStorage.setItem("devis", JSON.stringify(data));
  }
  /*==============================
  get devis 
  ================================*/
  getDevis() {
    const devis = localStorage.getItem("devis");
    return devis ? JSON.parse(devis) : null;
  }
  /*==============================
  conigs 
  ================================*/
  getConfigs() {
    const configs = localStorage.getItem("configs");
    return configs ? JSON.parse(configs) : null;
  }
  getSettings() {
    const settings = localStorage.getItem("settings");
    return settings ? JSON.parse(settings) : null;
  }
  getTarif() {
    const configs = localStorage.getItem("configs");
    return configs ? JSON.parse(configs).frais_deplacement : 0;
  }
  setConfigs(payload: any) {
    localStorage.setItem("configs", JSON.stringify(payload));
  }
  setSettings(payload: any) {
    localStorage.setItem("settings", JSON.stringify(payload));
  }
  getMapConfig() {
    const configs = localStorage.getItem("configs");
    if (configs) {
      const mapProvider = JSON.parse(configs!).map_type;
      return mapProvider ? mapProvider : "OPENROUTESERVICE";
    } else {
      return "OPENROUTESERVICE";
    }
  }
}
